import type { Animal, AnimalSpecies, ChatMessage, Crop, GameState, Guild, LeaderboardEntry, World } from './types';

const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';
const pendingChainKey = 'nst-ranch-pending-chain-actions';

type PendingChainAction = {
  txHash: string;
  action: 'buy' | 'plant' | 'harvest';
  cropType: Crop['id'];
  plotIndex: number | null;
};

export class ApiClient {
  private token = localStorage.getItem('nst-ranch-token') ?? '';

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('nst-ranch-token', token);
  }

  clearToken() {
    this.token = '';
    localStorage.removeItem('nst-ranch-token');
  }

  hasToken() {
    this.token = localStorage.getItem('nst-ranch-token') ?? '';
    return Boolean(this.token);
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${apiUrl}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        ...init.headers
      }
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 401) this.clearToken();
      throw new Error(body.message ?? `请求失败（${response.status}）`);
    }
    return body as T;
  }

  getNonce(address: string) {
    return this.request<{ message: string }>(`/api/auth/nonce/${address}`);
  }

  verify(address: string, message: string, signature: string) {
    return this.request<{ token: string }>('/api/auth/verify', {
      method: 'POST', body: JSON.stringify({ address, message, signature })
    });
  }

  getConfig() {
    return this.request<{ crops: Crop[] }>('/api/config');
  }

  getState() {
    return this.request<GameState>('/api/me');
  }

  private pendingChainActions() {
    try {
      return JSON.parse(localStorage.getItem(pendingChainKey) ?? '[]') as PendingChainAction[];
    } catch {
      localStorage.removeItem(pendingChainKey);
      return [];
    }
  }

  private savePendingChainActions(actions: PendingChainAction[]) {
    localStorage.setItem(pendingChainKey, JSON.stringify(actions));
  }

  async syncChainAction(txHash: string, action: 'buy' | 'plant' | 'harvest', cropType: Crop['id'], plotIndex: number | null) {
    const item = { txHash, action, cropType, plotIndex } satisfies PendingChainAction;
    try {
      return await this.request('/api/game/chain-sync', { method: 'POST', body: JSON.stringify(item) });
    } catch (error) {
      const pending = this.pendingChainActions();
      if (!pending.some((entry) => entry.txHash.toLowerCase() === txHash.toLowerCase())) {
        this.savePendingChainActions([...pending, item]);
      }
      throw error;
    }
  }

  async flushChainActions() {
    const pending = this.pendingChainActions();
    const failed: PendingChainAction[] = [];
    let synced = 0;
    for (const item of pending) {
      try {
        await this.request('/api/game/chain-sync', { method: 'POST', body: JSON.stringify(item) });
        synced += 1;
      } catch {
        failed.push(item);
      }
    }
    this.savePendingChainActions(failed);
    return { synced, remaining: failed.length };
  }

  daily() {
    return this.request('/api/game/daily', { method: 'POST', body: '{}' });
  }

  updateName(displayName: string) {
    return this.request('/api/me/profile', { method: 'PATCH', body: JSON.stringify({ displayName }) });
  }

  bindReferral(referrer: string) {
    return this.request('/api/social/referral', { method: 'POST', body: JSON.stringify({ referrer }) });
  }

  assist(targetAddress: string) {
    return this.request<{ reducedSeconds: number }>('/api/social/assist', {
      method: 'POST', body: JSON.stringify({ targetAddress })
    });
  }

  getLeaderboard() {
    return this.request<LeaderboardEntry[]>('/api/leaderboard');
  }

  getWorld() {
    return this.request<World>('/api/world');
  }

  getChat() {
    return this.request<ChatMessage[]>('/api/social/chat?limit=50');
  }

  sendChat(message: string) {
    return this.request<ChatMessage>('/api/social/chat', { method: 'POST', body: JSON.stringify({ message }) });
  }

  connectChat(onMessage: (message: ChatMessage) => void, onState: (connected: boolean) => void) {
    const socket = new WebSocket(`${apiUrl.replace(/^http/, 'ws')}/ws/chat`);
    socket.addEventListener('open', () => socket.send(JSON.stringify({ type: 'auth', token: this.token })));
    socket.addEventListener('message', (event) => {
      try {
        const payload = JSON.parse(String(event.data)) as { type?: string; message?: ChatMessage };
        if (payload.type === 'ready') onState(true);
        if (payload.type === 'chat' && payload.message) onMessage(payload.message);
      } catch { /* ignore malformed realtime payloads */ }
    });
    socket.addEventListener('close', () => onState(false));
    socket.addEventListener('error', () => onState(false));
    return socket;
  }

  getGuilds() {
    return this.request<Guild[]>('/api/social/guilds');
  }

  getMyGuild() {
    return this.request<Guild | null>('/api/social/guild');
  }

  createGuild(name: string, description: string) {
    return this.request<Guild>('/api/social/guilds', { method: 'POST', body: JSON.stringify({ name, description }) });
  }

  joinGuild(id: number) {
    return this.request<Guild>(`/api/social/guilds/${id}/join`, { method: 'POST', body: '{}' });
  }

  leaveGuild() {
    return this.request('/api/social/guild/leave', { method: 'POST', body: '{}' });
  }

  getAnimals() {
    return this.request<Animal[]>('/api/animals');
  }

  adoptAnimal(species: AnimalSpecies, name: string) {
    return this.request<Animal>('/api/animals/adopt', { method: 'POST', body: JSON.stringify({ species, name }) });
  }

  feedAnimal(id: number) {
    return this.request<Animal>(`/api/animals/${id}/feed`, { method: 'POST', body: '{}' });
  }

  collectAnimal(id: number) {
    return this.request<{ coins: number; xp: number }>(`/api/animals/${id}/collect`, { method: 'POST', body: '{}' });
  }
}

export const api = new ApiClient();
